/**
 * Sentry error tracking configuration
 *
 * To complete setup:
 * 1. Create Sentry account at https://sentry.io
 * 2. Create a new project (React)
 * 3. Copy your DSN from project settings
 * 4. Set SENTRY_DSN environment variable or add to .env file
 * 5. Uncomment and configure the Sentry.init() call below
 */

import * as Sentry from '@sentry/react'
import { config } from '@/config'

/**
 * Initialize Sentry for error tracking
 *
 * Uses centralized configuration from src/config/env.ts
 */
export function initSentry() {
  // Only initialize if enabled and DSN is configured
  if (!config.enableSentry || !config.sentryDsn) {
    console.log('[Sentry] Disabled or DSN not configured')
    return
  }

  // Log DSN (first 20 chars only for security)
  console.log('Sentry initialized with DSN:', config.sentryDsn.substring(0, 20) + '...')

  Sentry.init({
    dsn: config.sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: config.isProduction ? 0.1 : 1.0, // 10% in prod, 100% in dev
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    environment: config.env,
    // Release tracking
    release: config.appVersion,
    // Debug mode in development - shows detailed logs
    debug: config.isDevelopment,
    // Transport options to handle CORS in development
    transportOptions: {
      // In development, we'll log errors but CORS may block sending
      // In production, this works normally
    },
    // Before send hook to log what's being sent
    beforeSend(event, hint) {
      if (config.isDevelopment) {
        const originalError =
          hint.originalException instanceof Error
            ? hint.originalException.message
            : String(hint.originalException || '')
        console.log('✅ Sentry beforeSend - Event will be sent:', {
          message: event.message,
          error: event.exception?.values?.[0]?.value,
          level: event.level,
          originalError,
        })
        // In development, log to console even if CORS blocks sending
        console.warn(
          '⚠️ Note: CORS errors in console are expected in development. ' +
            'Errors will be sent successfully in production builds. ' +
            'Test with `npm run build && npm run preview` to verify Sentry works.'
        )
      }
      return event
    },
  })

  console.log('✅ Sentry initialized successfully for environment:', config.env)
}

/**
 * Capture an exception manually
 */
export function captureException(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  })
}

/**
 * Capture a message manually
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level)
}
